<template>
    <div>
        <h2
            v-t="'IntraKey-Zutrittsmedien'"
            class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText"
        ></h2>

        <table class="kgr-table kgr-table--striped table-groups kgr-row kgr-table--block">
            <thead>
                <tr class="kgr-table__row">
                    <th
                        v-t="'ID'"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                    <th
                        v-t="'Typ'"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                    <th
                        v-t="'Berechtigung'"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="accessMedium in accessMediums"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(accessMedium)">
                            <kgr-text-input
                                v-model="accessMedium.identifier"
                                :error="error.messageForField('identifier')"
                            />
                        </template>
                        <template v-else>
                            {{ accessMedium.identifier }}
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(accessMedium)">
                            <label class="kgr-radio kgr-radio--inline">
                                <input
                                    v-model="accessMedium.accessMediumType"
                                    type="radio"
                                    :name="accessMedium.id"
                                    :value="getIdCardValue()"
                                    :checked="isCard(accessMedium)"
                                    class="kgr-radio__control"
                                />
                                <span class="kgr-radio__label">{{ $t('Ausweis') }}</span>
                            </label>
                            <label class="kgr-radio kgr-radio--inline">
                                <input
                                    v-model="accessMedium.accessMediumType"
                                    type="radio"
                                    :name="accessMedium.id"
                                    :value="getChipValue()"
                                    :checked="isChip(accessMedium)"
                                    class="kgr-radio__control"
                                />
                                <span class="kgr-radio__label">{{ $t('Chip') }}</span>
                            </label>
                        </template>
                        <template v-else>
                            {{ translateAccessMediumType(accessMedium.accessMediumType) }}
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(accessMedium)">
                            <kgr-generic-select
                                v-model="accessMedium.permission"
                                display="name"
                                :options="clubPermissions"
                            />
                        </template>
                        <template v-else>
                            {{ null !== accessMedium.permission ? accessMedium.permission.name : '--' }}
                        </template>
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions">
                        <button
                            v-if="accessMedium.isEditable"
                            type="button"
                            class="kgr-table__action"
                            :disabled="isBeingEdited(accessMedium)"
                            @click="edit(accessMedium)"
                        >
                            <kgr-icon-edit
                                :size="15"
                                :disabled="isBeingEdited(accessMedium)"
                            />
                        </button>
                        <kgr-delete-dialog
                            :value="confirmDeleteId === accessMedium.id"
                            :object-name="accessMedium.identifier"
                            @post-input="onDeleteDialogInput"
                            @confirm="remove(accessMedium)"
                        >
                            <kgr-icon-delete
                                v-if="accessMedium.isDeletable"
                                :title="$t('Zutrittsmedium löschen')"
                                @click="showDeleteDialog(accessMedium)"
                            />
                        </kgr-delete-dialog>
                    </td>
                </tr>
                <tr
                    v-if="isCreatable"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        <kgr-text-input
                            v-model="newAccessMedium.identifier"
                            :error="error.messageForField('identifier')"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <label class="kgr-radio kgr-radio--inline">
                            <input
                                v-model="newAccessMedium.accessMediumType"
                                type="radio"
                                name="newAccessMedium"
                                :value="getIdCardValue()"
                                :checked="isCard(newAccessMedium)"
                                class="kgr-radio__control"
                            />
                            <span class="kgr-radio__label">{{ $t('Ausweis') }}</span>
                        </label>
                        <label class="kgr-radio kgr-radio--inline">
                            <input
                                v-model="newAccessMedium.accessMediumType"
                                type="radio"
                                name="newAccessMedium"
                                :value="getChipValue()"
                                :checked="isChip(newAccessMedium)"
                                class="kgr-radio__control"
                            />
                            <span class="kgr-radio__label">{{ $t('Chip') }}</span>
                        </label>
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-generic-select
                            v-model="newAccessMedium.permission"
                            display="name"
                            :value="null"
                            :options="clubPermissions"
                        />
                    </td>
                </tr>
            </tbody>
        </table>
        <kgr-button
            v-if="isCreatable"
            :value="$t('saveChanges')"
            :disabled="!isSavable()"
            :pending="pending"
            @click="save()"
        />
    </div>
</template>

<script>
import ClubMemberAccessMediumIntraKey from '../../../js/dto/ClubMemberAccessMediumIntraKey';
import ClubMemberAccessMediumIntraKeyRepository from '../../../js/repository/ClubMemberAccessMediumIntraKeyRepository';
import ClubPermissionIntraKeyRepository from '../../../js/repository/ClubPermissionIntraKeyRepository';
import DeleteDialog from '../../common/modal/DeleteDialog';
import Error from '../../../js/model/Error';
import GenericSelect from '../../common/form/select/GenericSelect';
import IconDelete from '../../common/icon/IconDelete';
import IconEdit from '../../common/icon/IconEdit';
import KgrButton from '../../common/form/button/KgrButton';
import TextInput from '../../common/form/input/TextInput';

const ID_CARD = 1;
const CHIP = 2;

export default {
    components: {
        'kgr-icon-edit': IconEdit,
        'kgr-icon-delete': IconDelete,
        'kgr-button': KgrButton,
        'kgr-generic-select': GenericSelect,
        'kgr-text-input': TextInput,
        'kgr-delete-dialog': DeleteDialog,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        memberId: {
            type: Number,
            required: true,
        },
        accessMediums: {
            type: Array,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            editAccessMediums: [],
            pending: false,
            confirmDeleteId: null,
            error: new Error(),
            jobDeleted: false,
            clubPermissions: [],
            newAccessMedium: this.buildNewAccessMedium(),
        };
    },
    created() {
        this.loadClubPermissions();
    },
    methods: {
        isBeingEdited(accessMedium) {
            return this.getEditIndex(accessMedium) !== -1;
        },
        getEditIndex(accessMedium) {
            return this.editAccessMediums.map((editAccessMedium) => editAccessMedium.id).indexOf(accessMedium.id);
        },
        edit(accessMedium) {
            if (this.isBeingEdited(accessMedium)) {
                return;
            }
            this.editAccessMediums.push(accessMedium);
        },
        remove(accessMedium) {
            ClubMemberAccessMediumIntraKeyRepository.delete(accessMedium.id)
                .then(() => {
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                })
                .catch(() => this.$notify.error('saveChanges.error'));
        },
        isSavable() {
            return this.editAccessMediums.length > 0 || this.newAccessMedium.isValid();
        },
        save() {
            if (this.newAccessMedium.isValid()) {
                this.create();
            }
            if (this.editAccessMediums.length > 0) {
                this.update();
            }
        },
        create() {
            this.pending = true;
            ClubMemberAccessMediumIntraKeyRepository.create(this.memberId, this.newAccessMedium)
                .then(() => {
                    this.newAccessMedium = this.buildNewAccessMedium();
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                })
                .catch((error) => {
                    if (error.status === 409) {
                        this.$notify.error('Das Zutrittsmedium mit der ID {id} existiert bereits.', { id: this.newAccessMedium.identifier });
                    } else {
                        this.$notify.error('saveChanges.error');
                    }
                    this.error = error;
                })
                .finally(() => this.pending = false);
        },
        update() {
            if (this.editAccessMediums.length === 0) {
                return;
            }
            this.pending = true;
            ClubMemberAccessMediumIntraKeyRepository.update(this.editAccessMediums)
                .then(() => {
                    this.editAccessMediums = [];
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                    this.error = new Error();
                })
                .catch((error) => {
                    this.$notify.error('saveChanges.error');
                    this.error = error;
                })
                .finally(() => this.pending = false);
        },
        onDeleteDialogInput() {
            this.confirmDeleteId = null;
        },
        showDeleteDialog(accessMedium) {
            this.confirmDeleteId = accessMedium.id;
        },
        closeAndAbort() {
            this.jobDeleted = true;
        },
        closeAndReload() {
            this.emitUpdated();
        },
        emitUpdated() {
            this.$emit('updated');
        },
        loadClubPermissions() {
            ClubPermissionIntraKeyRepository.find(this.clubId)
                .then((response) => this.clubPermissions = response);
        },
        translateAccessMediumType(accessMediumType) {
            return this.$t(`physicalAccess.accessMediumType.intraKey.${accessMediumType}`);
        },
        isCard(accessMedium) {
            return ID_CARD === Number(accessMedium.accessMediumType);
        },
        isChip(accessMedium) {
            return CHIP === Number(accessMedium.accessMediumType);
        },
        getIdCardValue() {
            return ID_CARD;
        },
        getChipValue() {
            return CHIP;
        },
        buildNewAccessMedium() {
            const newAccessMedium = new ClubMemberAccessMediumIntraKey();
            newAccessMedium.identifier = '';
            newAccessMedium.permission = null;
            return newAccessMedium;
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
</style>
